// Saved views for the Orders list ("views, not filters").
import { calendarDaysBetween, parseDay } from './format';
import { orderStatus } from './status';
import type { WorkOrder, ZenUser } from './types';

export type OrderViewKey =
  | 'active'
  | 'awaiting-invoice'
  | 'due-week'
  | 'overdue'
  | 'ready'
  | 'mine'
  | 'completed'
  | 'deleted';

export interface OrderView {
  key: OrderViewKey;
  label: string;
  adminOnly?: boolean;
}

export const ORDER_VIEWS: OrderView[] = [
  { key: 'active', label: 'Active' },
  { key: 'awaiting-invoice', label: 'Awaiting invoice' },
  { key: 'due-week', label: 'Due this week' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'ready', label: 'Ready for delivery' },
  { key: 'mine', label: 'Mine' },
  { key: 'completed', label: 'Completed' },
  { key: 'deleted', label: 'Deleted', adminOnly: true },
];

export function isOrderViewKey(value: string | null | undefined): value is OrderViewKey {
  return ORDER_VIEWS.some((view) => view.key === value);
}

function isLive(order: WorkOrder): boolean {
  return !order.deletedAt && order.status === 'active';
}

export function matchesOrderView(order: WorkOrder, view: OrderViewKey, user: ZenUser, now = new Date()): boolean {
  if (view === 'deleted') return Boolean(order.deletedAt);
  if (view === 'completed') return !order.deletedAt && order.status === 'completed';
  if (!isLive(order)) return false;
  const days = calendarDaysBetween(now, parseDay(order.deliveryDate));
  switch (view) {
    case 'active': return true;
    case 'awaiting-invoice': return !order.accountsApproved;
    case 'due-week': return days >= 0 && days < 7;
    case 'overdue': return days < 0;
    case 'ready': return orderStatus(order).tone === 'ready';
    case 'mine': return order.creatorId === user.id;
  }
}

export function ordersForView(orders: WorkOrder[], view: OrderViewKey, user: ZenUser, now = new Date()): WorkOrder[] {
  return orders.filter((order) => matchesOrderView(order, view, user, now));
}

/** Sales-only users see the orders they created; everyone else with order access sees all. */
export function scopeOrdersForUser(orders: WorkOrder[], user: ZenUser, salesOnly: boolean): WorkOrder[] {
  return salesOnly ? orders.filter((order) => order.creatorId === user.id) : orders;
}

/** Order value shown to billing users: the invoice when recorded, otherwise the quote. */
export function orderValue(order: WorkOrder): number | null {
  return order.invoice?.amount ?? order.quote?.amount ?? null;
}
